<template>
  <div class="voyage-compare-charts">
    <div v-for="chart in charts" :key="chart.id" class="voyage-compare-charts-item">
      <h6 class="voyage-compare-charts-title">
        <i :class="chart.icon"></i> {{ chart.title }}
      </h6>
      <VoyageCompareChart
        :chart-id="`voyage-compare-${chart.id}`"
        :timestamps="chart.timestamps"
        :datasets="chart.datasets"
        :y-label="chart.yLabel"
        :unit="chart.unit"
        :decimals="chart.decimals"
        :loading="loading"
        :start-seconds="startSeconds"
        :file-name="`voyage-compare-${chart.id}`"
      />
    </div>
    <p class="voyage-compare-charts-note">X axis: time since each voyage started.</p>
  </div>
</template>

<script>
import { apiDatetimeToUnix } from '@/util/apiDatetime';
import { useVoyageData } from '@/views/voyage/composables/useVoyageData';
import { extractRpmSeries, extractFlowmeterSeries, extractGpsSeries } from '@/views/voyage/utils/chartExtractors';
import { sharedAggregation, bucketSeconds, buildCompareSeries } from '@/views/voyage/utils/voyageCompareSeries';
import VoyageCompareChart from './VoyageCompareChart.vue';

const { loadTelemetryData } = useVoyageData();
const SIDE_KEYS = ['a', 'b'];
const EMPTY_ROWS = Object.freeze({ rpm: [], fuel: [], gps: [] });

// isi & urutan mirip PerformanceCharts detail voyage
const CHARTS = [
  {
    id: 'rpm',
    title: 'RPM Profile',
    icon: 'ri-settings-3-line',
    yLabel: 'RPM',
    unit: 'RPM',
    decimals: 0,
    source: 'rpm',
    metrics: [
      { field: 'portRpm', name: 'Port RPM' },
      { field: 'starboardRpm', name: 'Starboard RPM', dash: '6 4' },
    ],
  },
  {
    id: 'fuel',
    title: 'Fuel Rate Profile',
    icon: 'ri-drop-line',
    yLabel: 'Fuel Cons (L/hr)',
    unit: 'L/hr',
    decimals: 1,
    source: 'fuel',
    metrics: [{ field: 'totalFuelCons', name: 'ME Fuel' }],
  },
  {
    id: 'speed',
    title: 'Speed Profile',
    icon: 'ri-speed-line',
    yLabel: 'Speed (knots)',
    unit: 'kn',
    decimals: 2,
    source: 'gps',
    metrics: [{ field: 'speed', name: 'Speed' }],
  },
];

export default {
  name: 'VoyageCompareCharts',
  components: {
    VoyageCompareChart,
  },
  props: {
    // { a: master voyage, b: master voyage }
    voyages: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      loading: true,
      rows: { a: EMPTY_ROWS, b: EMPTY_ROWS },
    };
  },
  computed: {
    voyageKey() {
      return SIDE_KEYS.map((side) => this.voyages[side].mvygId).join('-');
    },
    startSeconds() {
      return {
        a: apiDatetimeToUnix(this.voyages.a.mvygStartTime),
        b: apiDatetimeToUnix(this.voyages.b.mvygStartTime),
      };
    },
    aggregation() {
      const duration = (side) => apiDatetimeToUnix(this.voyages[side].mvygEndTime) - this.startSeconds[side];
      return sharedAggregation(duration('a'), duration('b'));
    },
    charts() {
      const bucket = bucketSeconds(this.aggregation.aggregatedUnit);
      return CHARTS.map((chart) => ({
        ...chart,
        ...buildCompareSeries({
          sides: SIDE_KEYS.map((side) => ({
            side,
            rows: this.rows[side][chart.source],
            startSeconds: this.startSeconds[side],
          })),
          bucketSeconds: bucket,
          metrics: chart.metrics,
        }),
      }));
    },
  },
  watch: {
    voyageKey() {
      this.load();
    },
  },
  // bukan watch immediate: itu jalan sebelum created, token belum ada
  created() {
    this.requestToken = 0;
    this.load();
  },
  methods: {
    async load() {
      this.requestToken += 1;
      const token = this.requestToken;
      this.loading = true;
      const { aggregation } = this;
      // loadTelemetryData gak pernah throw, tipe yang gagal balik null
      const results = await Promise.all(SIDE_KEYS.map((side) => loadTelemetryData(this.voyages[side], aggregation)));
      if (token !== this.requestToken) return;
      const rows = {};
      SIDE_KEYS.forEach((side, index) => {
        const result = results[index];
        rows[side] = Object.freeze({
          rpm: extractRpmSeries(result.rpmData),
          fuel: extractFlowmeterSeries(result.flowmeterData),
          gps: extractGpsSeries(result.gpsData),
        });
      });
      this.rows = rows;
      this.loading = false;
    },
  },
};
</script>

<style scoped>
.voyage-compare-charts {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.voyage-compare-charts-item {
  padding: 8px 10px;
  background: var(--mz-surface-2);
  border: 1px solid var(--mz-border);
  border-radius: 6px;
}

.voyage-compare-charts-title {
  margin: 0 0 4px;
  font-size: var(--mz-fs-sm);
  font-weight: 600;
  color: var(--mz-text-muted);
  text-transform: uppercase;
}

.voyage-compare-charts-note {
  margin: 0;
  font-size: var(--mz-fs-sm);
  color: var(--mz-text-muted);
}
</style>
